import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {ArrowIcon} from '~/icons/arrow-icon'

import {ShopingExplore} from '~/icons/shopping-explore-icon'
import {getTranslation} from '~/localization/translation'
import type {RootAndListContext, RootLoaderResponse} from '~/types'

const ExploreEUSection = () => {
  const {locale, language, featureFlags, domain, trackAnalytics} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {customAffiliateLink},
    slug,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )
  const reportClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickoutCustom()

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'explore-more-products-banner',
    widget_place: 'bottom',
  })

  const handleCardClick = () => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout({
      index: 25,
      item_list_id: slug ?? '',
      click_element_name: 'explore-more-products-banner',
      partner_item_brand: '',
      partner_item_id: '',
      item_name: '',
      item_brand: '',
      click_id: customAffiliateLink?.clickId,
      affiliation: customAffiliateLink?.type ?? '',
      is_dws: false,
      location_id: locale,
      traffic: 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: customAffiliateLink?.url ?? '',
    })
    reportClick({
      widget_name: 'explore-more-products-banner',
      widget_place: 'bottom',
    })
  }

  return (
    <div
      ref={widgetRef}
      className={clsx(
        'border-gray relative z-20 flex h-full w-full flex-row items-center justify-between gap-2 overflow-hidden rounded-2xl border px-8 py-5 text-center tablet:flex-col-reverse tablet:px-5',
        stylesBydomain.LISTPAGE_SECTIONS_EXPLORE_BANNER_BG,
        'listpage_sections_explore_banner_bg',
      )}
    >
      <div className="flex w-74 flex-col gap-5 tablet:w-full">
        <span
          className={clsx(
            'text-start text-xl tablet:text-center',
            stylesBydomain.LISTPAGE_SECTIONS_EXPLORE_BANNER_TEXT,
            'listpage_sections_explore_banner_text',
          )}
        >
          {translations.ExploreShoppingSection.title}
        </span>
        <button
          type="button"
          aria-label={translations.ExploreShoppingSection.viewMoreDeals}
          className={clsx(
            'relative flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-3xl text-center capitalize',
            'text-base font-light',
            stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
            'listpage_button_primary',
          )}
        >
          <a
            href={customAffiliateLink?.url}
            onClick={handleCardClick}
            aria-label={translations.ExploreShoppingSection.viewMoreDeals}
            target="_blank"
            rel="sponsored noreferrer"
            className="absolute inset-0"
          />
          {translations.ExploreShoppingSection.viewMoreDeals}
          <ArrowIcon />
        </button>
      </div>
      <ShopingExplore
        className={clsx(
          'shrink-0',
          stylesBydomain.LISTPAGE_SECTIONS_EXPLORE_BANNER_ICONS,
          'listpage_sections_explore_banner_icons',
        )}
      />
    </div>
  )
}

export default ExploreEUSection
